import type { BrickCellKind } from "../data/ChapterData.js";

export interface BrickSparkleParticle {
  readonly x: number;
  readonly y: number;
  readonly radius: number;
  readonly alpha: number;
  readonly ray: number;
  readonly trailX: number;
  readonly trailY: number;
  readonly colorSlot: 0 | 1 | 2;
}

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);
const fract = (v: number): number => v - Math.floor(v);
const rand01 = (v: number): number => fract(Math.sin(v * 127.1) * 43758.5453123);

function particleCount(kind: BrickCellKind): number {
  if (kind === "mystery") return 7;
  return 0;
}

/**
 * mystery 벽돌의 반짝임을 매 프레임 파티클처럼 그리기 위한 순수 좌표 계산.
 * Graphics 하나에 다시 그리므로 GameObject/ParticleEmitter를 벽돌마다 만들지 않는다.
 */
export function resolveBrickSparkleParticles(
  kind: BrickCellKind,
  width: number,
  height: number,
  phaseSeed: number,
  timeMs: number,
): readonly BrickSparkleParticle[] {
  const count = particleCount(kind);
  if (count === 0 || width <= 0 || height <= 0) return [];

  const hw = width / 2;
  const hh = height / 2;
  const speed = 0.00155;
  const lift = height * 1.28;
  const scatterX = Math.max(6, width * 0.74);
  const scatterY = Math.max(2, height * 0.24);
  const baseRadius = Math.max(1.2, Math.min(width, height) * 0.055);

  return Array.from({ length: count }, (_, i) => {
    const seed = fract(phaseSeed + i * 0.173 + (i % 3) * 0.071);
    const raw = timeMs * speed + seed;
    const t = fract(raw);
    const cycle = Math.floor(raw);
    const fade = Math.sin(t * Math.PI);
    const randomSeed = phaseSeed * 977 + i * 131 + cycle * 59;
    const spawnX = (rand01(randomSeed + 0.11) * 2 - 1) * scatterX * 0.5;
    const spawnY = hh * (0.34 + rand01(randomSeed + 0.37) * 0.38);
    const drift = (rand01(randomSeed + 0.61) * 2 - 1) * scatterX * 0.28;
    const sway = Math.sin(timeMs * (0.004 + rand01(randomSeed + 0.83) * 0.004) + seed * 19) * scatterX * 0.08;
    const x = spawnX + drift * t + sway;
    const y = spawnY - t * lift + (rand01(randomSeed + 1.13) - 0.5) * scatterY * fade;
    const radius = baseRadius * (0.62 + rand01(randomSeed + 1.41) * 0.68) * (0.64 + fade * 0.42);
    const alpha = clamp01(0.56 * fade);
    const ray = radius * (1.65 + fade * 1.25);
    return {
      x: Math.max(-hw + radius, Math.min(hw - radius, x)),
      y: Math.max(-hh + radius, Math.min(hh - radius, y)),
      radius,
      alpha,
      ray,
      trailX: -drift * 0.08,
      trailY: Math.max(2, height * 0.13) * (0.35 + fade),
      colorSlot: Math.floor(rand01(randomSeed + 1.73) * 3) as 0 | 1 | 2,
    };
  });
}
